import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react";
import { useMemo } from "react";

import { assetName, formatPercent, useAssets } from "@/features/matches";

import type { CounterItem, EnemyCounter } from "../../model/HeroBuilds";
import { BuildItems } from "../BuildItems";

const TITLE = msg({ id: "builds.overview.counters", message: "Against whom" });
const HINT = msg({
  id: "builds.overview.counters_hint",
  message: "what {hero} players take noticeably more often when this hero is an enemy",
});
const TIMES = msg({
  id: "builds.overview.counter_times",
  message: "{times, plural, one {# time more often} other {#× more often}}",
});
const WINS = msg({ id: "builds.overview.counter_wins", message: "+{delta} wins" });
const WHY = msg({
  id: "builds.overview.counter_why",
  message: "Against {enemy} {share} take it, usually {usual}",
});
// Предмет — совет против врага, если его берут хотя бы во столько раз чаще обычного
// (`analysis.detectors.counter_items.MIN_UPLIFT`); иначе он совет по приросту побед.
const MIN_UPLIFT = 1.3;

type EnemyCountersProps = {
  counters: readonly EnemyCounter[];
  hero: string;
};

// «Против кого» (доска «19д»): вражеский герой и предметы, которые сильные на этом герое берут
// против него заметно чаще обычного, — по тем же правилам, что совет в разборе матча.
export const EnemyCounters = ({ counters, hero }: EnemyCountersProps) => {
  const { _, i18n } = useLingui();
  const { data } = useAssets();
  const heroes = useMemo(() => new Map((data?.heroes ?? []).map((h) => [h.id, h])), [data]);
  const items = useMemo(() => new Map((data?.items ?? []).map((i) => [i.id, i])), [data]);
  if (counters.length === 0) {
    return null;
  }
  const caption = (pick: CounterItem) => {
    const times = pick.usual_pick_rate > 0 ? pick.pick_rate / pick.usual_pick_rate : 0;
    if (times >= MIN_UPLIFT || pick.win_rate_delta === null) {
      return _({ ...TIMES, values: { times: Math.round(times * 10) / 10 } });
    }
    return _({ ...WINS, values: { delta: formatPercent(pick.win_rate_delta, i18n.locale) } });
  };
  return (
    <section aria-label={_(TITLE)} className="@container flex flex-col gap-3">
      <div className="mt-1 flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
        <h4 className="text-base font-semibold">{_(TITLE)}</h4>
        <span className="text-[13px] text-subtle">{_({ ...HINT, values: { hero } })}</span>
      </div>
      <ul className="grid gap-2.5 @2xl:grid-cols-2">
        {counters.map((counter) => {
          const enemy = heroes.get(counter.enemy_hero_id);
          const name = enemy === undefined ? `#${counter.enemy_hero_id}` : assetName(enemy.name);
          return (
            <li
              key={counter.enemy_hero_id}
              aria-label={name}
              className="flex items-start gap-3 rounded-xl border border-border bg-surface px-3 py-2.5"
            >
              <div className="flex w-[72px] shrink-0 flex-col items-center gap-1 pt-0.5 text-center">
                {enemy?.icon_url ? (
                  <img
                    src={enemy.icon_url}
                    alt=""
                    width={44}
                    height={44}
                    loading="lazy"
                    className="size-11 rounded-lg border border-border-strong bg-surface-raised object-cover"
                  />
                ) : (
                  <span
                    aria-hidden="true"
                    className="inline-flex size-11 items-center justify-center rounded-lg border border-border-strong bg-surface-raised font-display text-muted"
                  >
                    {name.slice(0, 1)}
                  </span>
                )}
                <span className="text-[12.5px] leading-tight font-semibold">{name}</span>
              </div>
              <BuildItems
                size={48}
                width={84}
                showPickRate={false}
                className="gap-x-2 gap-y-2.5"
                items={counter.items.map((pick) => {
                  const item = items.get(pick.item_id);
                  return {
                    itemId: pick.item_id,
                    atS: 0,
                    hideTime: true,
                    label: item === undefined ? undefined : assetName(item.name),
                    caption: caption(pick),
                    why: _({
                      ...WHY,
                      values: {
                        enemy: name,
                        share: formatPercent(pick.pick_rate, i18n.locale),
                        usual: formatPercent(pick.usual_pick_rate, i18n.locale),
                      },
                    }),
                  };
                })}
              />
            </li>
          );
        })}
      </ul>
    </section>
  );
};
